import React, { useCallback, useEffect, useMemo, useState } from "react";
import CtmsWorkspaceModal from "../../common/ctms/CtmsWorkspaceModal";
import Dropdown from "../../common/dropDown";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import {
  COMM_FILTER_ACTIONS,
  fetchCommFeasibilityDetail,
  fetchCommunicationTemplateById,
  fetchCommunicationTemplates,
  sendSmsEmailToVolunteers,
  volunteerByParticipant,
} from "./participantService";
import { formatParticipantDate } from "./participantReportUtils";

/** Feasibility communicate modal — Template → Review & Send. */
const STEPS = [
  { id: "template", label: "Template", icon: "fas fa-file-alt" },
  { id: "review", label: "Review & Send", icon: "fas fa-paper-plane" },
];

const KPI_ITEMS = [
  { key: "name", label: "Name of list", icon: "fas fa-list-ul" },
  { key: "size", label: "Participants", icon: "fas fa-users" },
  { key: "lastEmail", label: "Last email", icon: "fas fa-envelope" },
  { key: "lastSms", label: "Last SMS", icon: "fas fa-sms" },
];

function pick(row, ...keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

function toTemplateOptions(rows, type) {
  const needle = String(type || "").toLowerCase();
  return (Array.isArray(rows) ? rows : [])
    .filter((r) =>
      String(r.TemplateType || r.Type || "")
        .toLowerCase()
        .includes(needle),
    )
    .map((r) => ({
      label: r.TemplateName || r.Name || `${type} template`,
      value: String(r.TemplateId || r.Id || ""),
    }))
    .filter((o) => o.value);
}

/**
 * Large (not full-width) communicate modal — stepper Template → Review/Send.
 * Email or SMS only (mutually exclusive); KPIs on the right.
 */
export default function FeasibilityCommunicateModal({
  open,
  participantId,
  feasibilityName = "",
  participantSize = "",
  onClose,
  onSent,
}) {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [step, setStep] = useState(0);
  const [detail, setDetail] = useState(null);
  /** "email" | "sms" — one channel at a time */
  const [channel, setChannel] = useState("email");
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState("");
  const [selectedSmsTemplateId, setSelectedSmsTemplateId] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [overridePrefs, setOverridePrefs] = useState(true);
  const [campaignReason, setCampaignReason] = useState("");
  const [volList, setVolList] = useState([]);

  const emailEnabled = channel === "email";
  const smsEnabled = channel === "sms";

  const resetState = useCallback(() => {
    setStep(0);
    setDetail(null);
    setChannel("email");
    setEmailTemplates([]);
    setSmsTemplates([]);
    setSelectedEmailTemplateId("");
    setSelectedSmsTemplateId("");
    setEmailSubject("");
    setEmailBody("");
    setSmsBody("");
    setOverridePrefs(true);
    setCampaignReason("");
    setVolList([]);
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return undefined;
    }
    if (!participantId || !userData?.OrganizationId) return undefined;

    let cancelled = false;
    (async () => {
      showLoader("Loading communication...");
      try {
        const [commDetail, templateRows] = await Promise.all([
          fetchCommFeasibilityDetail(userData, participantId).catch(() => null),
          fetchCommunicationTemplates(userData).catch(() => []),
        ]);
        if (cancelled) return;

        setDetail(commDetail);
        const fromDetail = Array.isArray(commDetail?.templates)
          ? commDetail.templates
          : [];
        const list = fromDetail.length
          ? fromDetail
          : Array.isArray(templateRows)
            ? templateRows
            : [];
        setEmailTemplates(toTemplateOptions(list, "email"));
        setSmsTemplates(toTemplateOptions(list, "sms"));
      } catch (err) {
        if (!cancelled) {
          showAlert({
            message: err?.message || "Could not load communication setup.",
          });
        }
      } finally {
        if (!cancelled) hideLoader();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/participant only
  }, [open, participantId]);

  const summary = useMemo(() => {
    const participant = detail?.participant || {};
    const lastEmailRaw = pick(
      participant,
      "LastEmailDate",
      "lastEmailDate",
      "LastEmail",
      "lastEmail",
      "Last_Email_Date",
    );
    const lastSmsRaw = pick(
      participant,
      "LastSMSDate",
      "LastSmsDate",
      "lastSmsDate",
      "LastSMS",
      "lastSms",
      "Last_SMS_Date",
    );
    return {
      name:
        pick(participant, "ParticipantName", "participantName") ||
        feasibilityName ||
        "Feasibility",
      size:
        pick(participant, "ParticipantSize", "participantSize", "VolCount") ||
        (participantSize === 0 || participantSize
          ? String(participantSize)
          : "—"),
      lastEmail: lastEmailRaw ? formatParticipantDate(lastEmailRaw) : "—",
      lastSms: lastSmsRaw ? formatParticipantDate(lastSmsRaw) : "—",
    };
  }, [detail, feasibilityName, participantSize]);

  const selectedEmailLabel =
    emailTemplates.find((o) => o.value === selectedEmailTemplateId)?.label ||
    "—";
  const selectedSmsLabel =
    smsTemplates.find((o) => o.value === selectedSmsTemplateId)?.label || "—";

  const loadTemplateBody = async (templateId, templateType) => {
    if (!templateId) {
      if (templateType === "Email") {
        setEmailSubject("");
        setEmailBody("");
      } else {
        setSmsBody("");
      }
      return;
    }
    showLoader("Loading template...");
    try {
      const row = await fetchCommunicationTemplateById(
        userData,
        templateId,
        templateType,
      );
      if (templateType === "Email") {
        setEmailSubject(row?.EmailSubject || row?.SubjectName || "");
        setEmailBody(row?.TemplateText || row?.EmailTemplateText || "");
      } else {
        setSmsBody(row?.SMSTemplateText || row?.TemplateText || "");
      }
    } catch (err) {
      showAlert({ message: err?.message || "Could not load template." });
    } finally {
      hideLoader();
    }
  };

  const handleChannelChange = (next) => {
    if (next === channel) return;
    setChannel(next);
    setVolList([]);
    setStep(0);
  };

  const canContinueToReview = () => {
    if (smsEnabled && !selectedSmsTemplateId) {
      showAlert({ message: "Please select an SMS template." });
      return false;
    }
    if (emailEnabled && !selectedEmailTemplateId) {
      showAlert({ message: "Please select an Email template." });
      return false;
    }
    return true;
  };

  const handleApplyTemplate = async () => {
    if (!canContinueToReview()) return;
    if (!participantId) {
      showAlert({ message: "Feasibility id is required." });
      return;
    }

    const gender = pick(detail?.participant, "Gender", "gender");
    showLoader("Loading participants...");
    try {
      const rows = await volunteerByParticipant(userData, {
        ParticipantId: participantId,
        Gender: gender,
        ContactedInLastWeek: 0,
        ContactedInLastMonth: 0,
        SMSWithinDays: 0,
        EmailWithinDays: 0,
        ActionType: COMM_FILTER_ACTIONS.GET_VOLUNTEERS,
        FilterName: "",
        FilterId: "",
      });
      const mapped = (Array.isArray(rows) ? rows : [])
        .map((row) => {
          const volunteerId = pick(
            row,
            "VolunteerDemRecordId",
            "VolunteerId",
            "volunteerDemRecordId",
          );
          const emailFlag = row?.IsVolEmail ?? row?.isVolEmail;
          const smsFlag = row?.IsVolSMS ?? row?.isVolSMS;
          return {
            Email: pick(row, "EmailId", "Email", "email"),
            Mobile: pick(row, "ContactNumber", "Mobile", "mobile"),
            Name: pick(row, "VolunteerName", "Name", "name"),
            VolunteerId: volunteerId ? volunteerId.toUpperCase() : "",
            IsVolEmail:
              emailFlag === true ||
              emailFlag === 1 ||
              emailFlag === "1" ||
              /^true|yes$/i.test(String(emailFlag ?? "")),
            IsVolSMS:
              smsFlag === true ||
              smsFlag === 1 ||
              smsFlag === "1" ||
              /^true|yes$/i.test(String(smsFlag ?? "")),
          };
        })
        .filter((row) => row.VolunteerId);
      if (!mapped.length) {
        showAlert({ message: "No volunteer found for this feasibility." });
        setVolList([]);
        return;
      }
      setVolList(mapped);
      setStep(1);
    } catch (err) {
      showAlert({
        message: err?.message || "Could not load participants for send.",
      });
      setVolList([]);
    } finally {
      hideLoader();
    }
  };

  const handleSend = async () => {
    if (!participantId) {
      showAlert({ message: "Feasibility id is required." });
      return;
    }
    if (!canContinueToReview()) return;
    if (!volList.length) {
      showAlert({
        message:
          "There should be at least 1 participant for Total SMS or Email to be sent.",
      });
      return;
    }

    showLoader("Sending communication...");
    try {
      await sendSmsEmailToVolunteers(userData, {
        SMS: smsEnabled,
        Email: emailEnabled,
        SMSText: smsEnabled ? smsBody : "",
        EmailText: emailEnabled ? emailBody : "",
        EmailSubject: emailEnabled ? emailSubject : "",
        ParticipantId: String(participantId || "").trim().toUpperCase(),
        FilterId: null,
        SMSTemplateId: smsEnabled ? selectedSmsTemplateId || null : null,
        EmailTemplateId: emailEnabled ? selectedEmailTemplateId || null : null,
        IsOverRideCommPre: overridePrefs,
        CampaignReason: campaignReason,
        VolList: volList,
      });
      showAlert({ message: "Communication sent.", type: "success" });
      onSent?.();
      onClose?.();
    } catch (err) {
      showAlert({ message: err?.message || "Could not send communication." });
    } finally {
      hideLoader();
    }
  };

  const recipientCount = volList.length || Number(summary.size) || 0;

  const kpiValues = {
    name: summary.name,
    size: summary.size,
    lastEmail:
      summary.lastEmail && summary.lastEmail !== "—" ? summary.lastEmail : "—",
    lastSms:
      summary.lastSms && summary.lastSms !== "—" ? summary.lastSms : "—",
  };

  const goToStep = (index) => {
    if (index === 0) {
      setStep(0);
      return;
    }
    if (index === 1) {
      if (!volList.length) {
        handleApplyTemplate();
        return;
      }
      if (!canContinueToReview()) return;
      setStep(1);
    }
  };

  return (
    <>
      <CtmsWorkspaceModal
        open={open}
        title="Send Communication"
        titleId="pm-feas-communicate-title"
        onClose={onClose}
        className="ctms-workspace-modal--compact-host"
        dialogClassName="ctms-workspace-modal__dialog--feas-comm"
        contentClassName="pm-feas-comm-modal"
      >
        <div className="pm-feas-comm">
          <div className="pm-feas-comm__layout">
            <div className="pm-feas-comm__main">
              <nav className="pm-feas-comm__track" aria-label="Communication steps">
                {STEPS.map((item, index) => {
                  const isActive = step === index;
                  const isComplete = step > index;
                  return (
                    <React.Fragment key={item.id}>
                      <button
                        type="button"
                        className={`pm-feas-comm__node${
                          isActive ? " is-active" : ""
                        }${isComplete ? " is-complete" : ""}`}
                        onClick={() => goToStep(index)}
                      >
                        <span
                          className="pm-feas-comm__node-circle"
                          aria-hidden="true"
                        >
                          {isComplete ? (
                            <i className="fas fa-check" />
                          ) : (
                            <i className={item.icon} />
                          )}
                        </span>
                        <span className="pm-feas-comm__node-label">
                          {item.label}
                        </span>
                      </button>
                      {index < STEPS.length - 1 ? (
                        <div
                          className={`pm-feas-comm__connector${
                            isComplete ? " is-complete" : ""
                          }${isActive ? " is-current" : ""}`}
                          aria-hidden="true"
                        >
                          <span className="pm-feas-comm__connector-line" />
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </nav>

              {step === 0 ? (
                <div className="pm-feas-comm__panel">
                  <div className="pm-feas-comm__panel-head">
                    <h3>Choose channel & template</h3>
                    <p>Select Email or SMS, then pick a template to preview.</p>
                  </div>

                  <div
                    className="pm-feas-comm__segment"
                    role="radiogroup"
                    aria-label="Channel"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={emailEnabled}
                      className={`pm-feas-comm__segment-btn${
                        emailEnabled ? " is-active" : ""
                      }`}
                      onClick={() => handleChannelChange("email")}
                    >
                      <i className="fas fa-envelope" aria-hidden="true" />
                      Email
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={smsEnabled}
                      className={`pm-feas-comm__segment-btn${
                        smsEnabled ? " is-active" : ""
                      }`}
                      onClick={() => handleChannelChange("sms")}
                    >
                      <i className="fas fa-sms" aria-hidden="true" />
                      SMS
                    </button>
                  </div>

                  {emailEnabled ? (
                    <div className="pm-feas-comm__block">
                      <label className="pm-feas-comm__label">
                        Email template
                      </label>
                      <Dropdown
                        options={emailTemplates}
                        selected={
                          emailTemplates.find(
                            (o) => o.value === selectedEmailTemplateId,
                          ) || null
                        }
                        onSelect={(opt) => {
                          const id = opt?.value || "";
                          setSelectedEmailTemplateId(id);
                          loadTemplateBody(id, "Email");
                        }}
                      />
                      <div className="pm-feas-comm__preview">
                        <div className="pm-feas-comm__preview-head">
                          <i className="fas fa-eye" aria-hidden="true" />
                          Preview
                        </div>
                        <input
                          className="form-control pm-feas-comm__subject"
                          value={emailSubject}
                          readOnly
                          placeholder="Subject"
                        />
                        {emailBody ? (
                          <div
                            className="pm-feas-comm__email-body"
                            dangerouslySetInnerHTML={{ __html: emailBody }}
                          />
                        ) : (
                          <div className="pm-feas-comm__email-body is-empty">
                            Select a template to preview
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="pm-feas-comm__block">
                      <label className="pm-feas-comm__label">SMS template</label>
                      <Dropdown
                        options={smsTemplates}
                        selected={
                          smsTemplates.find(
                            (o) => o.value === selectedSmsTemplateId,
                          ) || null
                        }
                        onSelect={(opt) => {
                          const id = opt?.value || "";
                          setSelectedSmsTemplateId(id);
                          loadTemplateBody(id, "SMS");
                        }}
                      />
                      <div className="pm-feas-comm__preview">
                        <div className="pm-feas-comm__preview-head">
                          <i className="fas fa-eye" aria-hidden="true" />
                          Preview
                        </div>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={smsBody}
                          readOnly
                          placeholder="Select a template to preview the message"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {step === 1 ? (
                <div className="pm-feas-comm__panel">
                  <div className="pm-feas-comm__panel-head">
                    <h3>You&apos;re all set to send</h3>
                    <p>
                      Confirm recipients and preferences, then send to the
                      participant list.
                    </p>
                  </div>

                  <div className="pm-feas-comm__review-grid">
                    <div className="pm-feas-comm__review-card">
                      <span>
                        {emailEnabled ? "Total emails" : "Total SMS"}
                      </span>
                      <strong>{recipientCount}</strong>
                    </div>
                    <div className="pm-feas-comm__review-card">
                      <span>
                        {emailEnabled ? "Email template" : "SMS template"}
                      </span>
                      <strong
                        title={
                          emailEnabled ? selectedEmailLabel : selectedSmsLabel
                        }
                      >
                        {emailEnabled ? selectedEmailLabel : selectedSmsLabel}
                      </strong>
                    </div>
                  </div>

                  <div className="pm-feas-comm__block">
                    <label className="pm-feas-comm__label">
                      Reason of campaign
                    </label>
                    <textarea
                      className="form-control"
                      rows={2}
                      placeholder="Optional reason for this campaign"
                      value={campaignReason}
                      onChange={(e) => setCampaignReason(e.target.value)}
                    />
                  </div>

                  <label className="pm-feas-comm__switch">
                    <input
                      type="checkbox"
                      checked={overridePrefs}
                      onChange={(e) => setOverridePrefs(e.target.checked)}
                    />
                    <span>
                      Override communication preferences for these participants
                    </span>
                  </label>
                </div>
              ) : null}

              <div className="pm-feas-comm__footer">
                {step === 1 ? (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setStep(0)}
                  >
                    Back
                  </button>
                ) : (
                  <span />
                )}
                {step === 0 ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleApplyTemplate}
                  >
                    Next
                    <i className="fas fa-arrow-right ms-2" aria-hidden="true" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSend}
                  >
                    <i
                      className="fas fa-paper-plane me-1"
                      aria-hidden="true"
                    />
                    Send to participant list
                  </button>
                )}
              </div>
            </div>

            <aside
              className="pm-feas-comm__kpis"
              aria-label="Feasibility summary"
            >
              <div className="pm-feas-comm__kpi-card">
                <div className="pm-feas-comm__kpi-title">Summary</div>
                {KPI_ITEMS.map((item) => (
                  <div key={item.key} className="pm-feas-comm__kpi-row">
                    <span className="pm-feas-comm__kpi-icon" aria-hidden="true">
                      <i className={item.icon} />
                    </span>
                    <div className="pm-feas-comm__kpi-copy">
                      <span>{item.label}</span>
                      <strong title={String(kpiValues[item.key] || "")}>
                        {kpiValues[item.key]}
                      </strong>
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </CtmsWorkspaceModal>

      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}
